import i18n from 'i18n'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { vaultsAPI, type VaultRow } from 'api/vaults'
import { branchChanged } from './branchSlice'

interface VaultsState {
 data: VaultRow[]
 loading: boolean
 error: string | null
}

const initialState: VaultsState = {
 data: [],
 loading: false,
 error: null,
}

export const fetchVaults = createAsyncThunk(
 'vaults/fetchVaults',
 async (params: { active_only?: boolean } = {}, { rejectWithValue }) => {
 try {
 const response = await vaultsAPI.list(params)
 return response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.vaults.fetchFailed'))
 }
 }
)

export const createVault = createAsyncThunk(
 'vaults/createVault',
 async (payload: Record<string, unknown>, { rejectWithValue }) => {
 try {
 const response = await vaultsAPI.create(payload)
 return response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.vaults.createFailed'))
 }
 }
)

export const updateVault = createAsyncThunk(
 'vaults/updateVault',
 async ({ id, data }: { id: string; data: Record<string, unknown> }, { rejectWithValue }) => {
 try {
 const response = await vaultsAPI.update(id, data)
 return response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.vaults.updateFailed'))
 }
 }
)

export const deleteVault = createAsyncThunk(
 'vaults/deleteVault',
 async (id: string, { rejectWithValue }) => {
 try {
 await vaultsAPI.delete(id)
 return id
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.vaults.deleteFailed'))
 }
 }
)

export const depositToVault = createAsyncThunk(
 'vaults/deposit',
 async ({ vaultId, payload }: {
  vaultId: string
  payload: {
   amount: number
   classification: 'customer_collection' | 'other_income' | 'refund_recovery' | 'other_external_source'
   date?: string
   reference?: string
   notes?: string
   idempotency_key?: string
  }
 }, { rejectWithValue }) => {
 try {
 const response = await vaultsAPI.deposit(vaultId, payload)
 return response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.vaults.depositFailed'))
 }
 }
)

export const withdrawFromVault = createAsyncThunk(
 'vaults/withdraw',
 async ({ vaultId, payload }: {
  vaultId: string
  payload: {
   amount: number
   classification: 'operating_expense' | 'other_external_use'
   date?: string
   reference?: string
   notes?: string
   idempotency_key?: string
  }
 }, { rejectWithValue }) => {
 try {
 const response = await vaultsAPI.withdraw(vaultId, payload)
 return response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.vaults.withdrawFailed'))
 }
 }
)

export const transferBetweenVaults = createAsyncThunk(
 'vaults/transfer',
 async (payload: { from_vault_id: string; to_vault_id: string; amount: number; date?: string; reference?: string; notes?: string }, { rejectWithValue }) => {
 try {
 const response = await vaultsAPI.transfer(payload)
 return response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.vaults.transferFailed'))
 }
 }
)

const vaultsSlice = createSlice({
 name: 'vaults',
 initialState,
 reducers: {
 clearVaults: (state) => {
 state.data = []
 state.error = null
 },
 },
 extraReducers: (builder) => {
 builder
 .addCase(fetchVaults.pending, (state) => { state.loading = true; state.error = null })
 .addCase(fetchVaults.fulfilled, (state, action) => {
 state.loading = false
 state.data = action.payload
 })
 .addCase(fetchVaults.rejected, (state, action) => { state.loading = false; state.error = action.payload as string })
 .addCase(createVault.fulfilled, (state, action) => {
 if (action.payload) state.data.push(action.payload)
 })
 .addCase(updateVault.fulfilled, (state, action) => {
 const idx = state.data.findIndex((v) => v.id === action.payload?.id)
 if (idx !== -1 && action.payload) state.data[idx] = action.payload
 })
 .addCase(deleteVault.fulfilled, (state, action) => {
 state.data = state.data.filter((v) => v.id !== action.payload)
 })
 .addCase(branchChanged, (state) => {
 Object.assign(state, initialState)
 })
 },
})

export const { clearVaults } = vaultsSlice.actions
export default vaultsSlice.reducer
